/**
 *
 * ShiftDetailCardAssignmentStatus
 *
 */

import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSnackbar } from '~/containers/Snackbar/SnackbarContext';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';

import CheckIcon from '@mui/icons-material/Check';
import DotIcon from '@mui/icons-material/FiberManualRecord';
import PublicIcon from '@mui/icons-material/Public';

import { yellow, green, blue } from '@mui/material/colors';

import ConfirmationModal from '~/components/ConfirmationModal';
import commonMessages from '~/containers/App/messages';
import { useFinalizeShift } from '~/hooks/api/useFinalizeShift';
import { useCommitShift } from '~/hooks/api/useCommitShift';
import { CHANGED, COMMITTED, FINALIZED } from './constants';
import messages from './messages';
import styles from './styles.module.css';

const getColorForState = (state: string) => {
  switch (state) {
    case CHANGED:
      return yellow[800];
    case COMMITTED:
      return blue[800];
    case FINALIZED:
      return green[800];
    default:
      return 'black';
  }
};

const getTransitionNameForState = (state: string) => {
  switch (state) {
    case CHANGED:
      return 'commit';
    case COMMITTED:
      return 'finalize';
    default:
      return undefined;
  }
};

const getTransitionActionForState = (
  state: string,
  {
    onCommit,
    showConfirmFinalizationDialog,
  }: { onCommit: () => void; showConfirmFinalizationDialog: () => void }
) => {
  switch (state) {
    case CHANGED:
      return onCommit;
    case COMMITTED:
      return showConfirmFinalizationDialog;
    default:
      return undefined;
  }
};

const getTransitionIconForState = (state: string) => {
  switch (state) {
    case CHANGED:
      return <CheckIcon />;
    case COMMITTED:
      return <PublicIcon />;
    default:
      return undefined;
  }
};

const hasStateTransition = (state: string) => ![FINALIZED].includes(state);

type ShiftDetailCardAssignmentStatusProps = {
  shiftId: number;
  state: string;
  shiftHasPlanningErrors: boolean;
};

export const ShiftDetailCardAssignmentStatus = (
  props: ShiftDetailCardAssignmentStatusProps
) => {
  const [
    isConfirmFinalizationDialogVisible,
    setIsConfirmFinalizationDialogVisible,
  ] = React.useState(false);
  const hideConfirmFinalizationDialog = () =>
    setIsConfirmFinalizationDialogVisible(false);
  const showConfirmFinalizationDialog = () =>
    setIsConfirmFinalizationDialogVisible(true);
  const intl = useIntl();
  const { showSnackbar } = useSnackbar();
  const commitShiftMutation = useCommitShift({});
  const finalizeShiftMutation = useFinalizeShift({
    onError: () => {
      showSnackbar({
        message: intl.formatMessage(messages.finalizeShiftFailed),
      });
    },
    onSuccess: () => {
      showSnackbar({
        message: intl.formatMessage(messages.finalizeShiftSucceeded),
      });
    },
    onSettled: () => {
      hideConfirmFinalizationDialog();
    },
  });
  const transitionName = getTransitionNameForState(props.state);
  return (
    <Card>
      <Typography variant="subtitle2" style={{ padding: 16 }}>
        <FormattedMessage {...messages.title} />
      </Typography>
      <div className={styles.status}>
        <DotIcon style={{ color: getColorForState(props.state) }} />
        <CardHeader
          title={intl.formatMessage(
            messages[props.state.toLowerCase() as keyof typeof messages]
          )}
          subheader={intl.formatMessage(
            messages[
              `${props.state.toLowerCase()}Description` as keyof typeof messages
            ]
          )}
          style={{ paddingTop: 0, paddingBottom: 0 }}
        />
      </div>
      {hasStateTransition(props.state) && transitionName && (
        <CardActions style={{ padding: 16, paddingTop: 0 }}>
          <Button
            variant="contained"
            color="primary"
            disabled={
              commitShiftMutation.isPending ||
              (props.state === COMMITTED && props.shiftHasPlanningErrors)
            }
            startIcon={
              commitShiftMutation.isPending
                ? undefined
                : getTransitionIconForState(props.state)
            }
            onClick={getTransitionActionForState(props.state, {
              onCommit: () => commitShiftMutation.mutate({ id: props.shiftId }),
              showConfirmFinalizationDialog,
            })}
          >
            {commitShiftMutation.isPending
              ? intl.formatMessage(commonMessages.loading)
              : intl.formatMessage(messages[transitionName])}
          </Button>
        </CardActions>
      )}
      <ConfirmationModal
        open={isConfirmFinalizationDialogVisible}
        title={intl.formatMessage(messages.finalizeConfirmTitle)}
        text={<FormattedMessage {...messages.finalizeConfirmText} />}
        confirmLabel={<FormattedMessage {...messages.finalizeConfirmButton} />}
        cancelLabel={<FormattedMessage {...messages.finalizeConfirmCancel} />}
        onCancel={hideConfirmFinalizationDialog}
        onConfirm={() => finalizeShiftMutation.mutate({ id: props.shiftId })}
      />
    </Card>
  );
};

export default ShiftDetailCardAssignmentStatus;
